import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import { ImageUploaderField, TextField } from '@extensions/components/Form';
import { Width } from '@extensions/AttributePanel/components/attributes/Width';
import { Height } from '@extensions/AttributePanel/components/attributes/Height';
import { Align } from '@extensions/AttributePanel/components/attributes/Align';
import { ContainerBackgroundColor } from '@extensions/AttributePanel/components/attributes/ContainerBackgroundColor';
import { BorderWidth } from '@extensions/AttributePanel/components/attributes/BorderWidth';
import { BorderStyle } from '@extensions/AttributePanel/components/attributes/BorderStyle';
import { BorderColor } from '@extensions/AttributePanel/components/attributes/BorderColor';
import { Link } from '@extensions/AttributePanel/components/attributes/Link';
import { MergeTags } from '@extensions/AttributePanel/components/attributes/MergeTags';

import { Collapse, Grid, Input, Space } from '@arco-design/web-react';
import { useBlock, useEditorProps, useFocusIdx, TextStyle } from '@bay-email/editor';
import BaySwitch from '@bay-ui/BaySwitch';
import BayPillToggle from '@bay-ui/BayPillToggle';
import BayTooltip from '@bay-ui/BayTooltip';
import { BsQuestionCircle } from 'react-icons/bs';
import { useField } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { imageHeightAdapter, pixelAdapter } from '../../adapter';
import {
	t,
	DYNAMIC_IMAGE_DEFAULT_ASPECT,
	getDynamicImageFieldKind,
	getMergeTagDisplayLabel,
} from '@bay-email/core';
import { useSelectedNode } from '@extensions/hooks/useSelectedNode';
import { useImageNaturalSize } from '@extensions/hooks/useImageNaturalSize';
import { getMaxContentWidth } from '@extensions/utils/getMaxContentWidth';
import { AttachedListingSection } from '../shared/AttachedListingSection';
import { usesListingMergeTags } from '../shared/listingAttachment';
import { useClearAttachedListingWhenUnused } from '../shared/useClearAttachedListingWhenUnused';

type ImageMode = 'static' | 'dynamic';

/** Pixels from "120" / "120px"; null for empty, "auto" or any other unit. */
function toPixelNumber(value: unknown): number | null {
	const match = String(value ?? '')
		.trim()
		.toLowerCase()
		.match(/^(\d+(?:\.\d+)?)(px)?$/);
	return match ? Number(match[1]) : null;
}

interface ImagePanelProps {
	forceMode?: ImageMode;
	hideModeToggle?: boolean;
	contentHeader?: string;
}

export function Image({
	forceMode,
	hideModeToggle = false,
	contentHeader,
}: ImagePanelProps = {}) {
	const { focusIdx } = useFocusIdx();
	const { change, values } = useBlock();
	const { onUploadImage } = useEditorProps();
	const { selectedNode } = useSelectedNode();
	const { size: naturalSize, status: naturalSizeStatus } = useImageNaturalSize(
		selectedNode?.attributes?.src,
	);
	const { input: imageTypeInput } = useField(`${focusIdx}.data.value.imageType`, {
		parse: v => v,
	});
	const { input: dynamicImageTagInput } = useField(
		`${focusIdx}.data.value.dynamicImageTag`,
		{ parse: v => v },
	);
	const { input: dynamicImageIdInput } = useField(
		`${focusIdx}.data.value.dynamicImageId`,
		{ parse: v => v },
	);
	const { input: fluidOnMobileInput } = useField(
		`${focusIdx}.attributes.fluid-on-mobile`,
		{ parse: v => v },
	);

	const normalizeDynamicImageTagValue = useCallback((value: unknown) => {
		const normalized = String(value || '').trim();
		if (!normalized) return '';
		const clean = normalized.replace(/^\{\{|\}\}$/g, '').trim();
		return clean ? `{{${clean}}}` : '';
	}, []);

	const normalizedDynamicTag = useMemo(
		() => normalizeDynamicImageTagValue(dynamicImageTagInput.value),
		[dynamicImageTagInput.value, normalizeDynamicImageTagValue],
	);

	// Resolved before the dimension logic below, which is static-only: a dynamic
	// image's real src is a merge tag resolved at send time, so `src` here is
	// only the fallback. Locking the ratio or the pixel bounds to the fallback's
	// shape would constrain an image we have never seen.
	const effectiveMode: ImageMode = useMemo(() => {
		if (forceMode) return forceMode;

		if (imageTypeInput.value === 'dynamic') return 'dynamic';
		if (imageTypeInput.value === 'static') return 'static';

		return normalizedDynamicTag ? 'dynamic' : 'static';
	}, [forceMode, imageTypeInput.value, normalizedDynamicTag]);

	const isStaticMode = effectiveMode === 'static';

	// Static only. In dynamic mode `src` is just the fallback, so its cropped
	// dimensions describe an image the recipient may never see — while Width and
	// Height below size the *container* the real asset drops into. Showing both
	// side by side read as one contradicting the other.
	const dimensionHeader =
		isStaticMode && naturalSize
			? `${t('Dimension')} (${naturalSize.width} × ${naturalSize.height})`
			: t('Dimension');

	/**
	 * The aspect ratio everything else is derived from. It comes from the
	 * asset and only from the asset: cropping replaces the src with the
	 * cropped raster, so its natural size already IS the cropped size. Typing
	 * in the width or height fields must never move it.
	 */
	const aspectRatio =
		isStaticMode && naturalSize && naturalSize.height > 0
			? naturalSize.width / naturalSize.height
			: null;

	// Column width minus the horizontal padding of everything it sits in. This
	// is the hard ceiling for a custom width in BOTH modes — a dynamic field's
	// box is still bounded by the column it lives in.
	const maxWidth = useMemo(
		() => getMaxContentWidth(values, focusIdx),
		[values, focusIdx],
	);

	/**
	 * Which kind of dynamic field this is, and therefore how the real
	 * per-record asset gets fitted into the box. Fixed by field type, never a
	 * user choice — see core `dynamicImageFit`.
	 */
	const dynamicFieldKind = isStaticMode
		? null
		: getDynamicImageFieldKind(normalizedDynamicTag);
	const isCircleField = dynamicFieldKind === 'circle';
	// Every recognised dynamic field carries explicit pixels for its whole
	// lifecycle, so none of them offer Auto. This is wider than the fit rule
	// alone would need — contain could let a height scale — and deliberately so.
	const requiresExplicitSize = Boolean(dynamicFieldKind);

	// Static only: the calculated numbers shown in place of the word "Auto".
	// A dynamic field has no knowable rendered size — the asset varies per
	// record — so it keeps the plain Auto label.
	const renderedWidth = isStaticMode ? maxWidth : null;
	const renderedHeight =
		renderedWidth && aspectRatio ? Math.round(renderedWidth / aspectRatio) : null;

	const widthPlaceholder = renderedWidth ? String(renderedWidth) : undefined;
	const heightPlaceholder = renderedHeight ? String(renderedHeight) : undefined;

	/**
	 * Static sizing needs an aspect ratio to keep the two fields in step;
	 * without one, a typed number would store width OR height alone, and that
	 * half-set pair renders down a path that scales proportionally and caps at
	 * the column — so the canvas would stop matching the panel.
	 *
	 * Keyed on a load that actually FAILED, not merely one in flight. Both give
	 * a null aspect ratio, but greying the inputs for the duration of every
	 * image load would contradict "the moment an image is uploaded, Width and
	 * Height should appear".
	 */
	const staticSizingUnavailable = isStaticMode && naturalSizeStatus === 'error';

	/**
	 * Nothing to size yet, so Width and Height are hidden outright rather than
	 * shown empty: no image uploaded in static mode, no source picked in
	 * dynamic mode. They appear the moment the block is configured.
	 */
	const hasImageToSize = isStaticMode
		? Boolean(String(selectedNode?.attributes?.src || '').trim())
		: Boolean(normalizedDynamicTag);

	const clampWidth = useCallback(
		(px: number) => (maxWidth ? Math.min(px, maxWidth) : px),
		[maxWidth],
	);

	const setBothToAuto = useCallback(() => {
		change(`${focusIdx}.attributes.width`, 'auto');
		change(`${focusIdx}.attributes.height`, 'auto');
	}, [change, focusIdx]);

	/**
	 * Whether the designer has typed a dimension on this block. System-written
	 * defaults and seeds do not count — this is set only from
	 * `onValueCommitted`, which fires solely from the inputs.
	 *
	 * It is what keeps "seed from the first fallback" meaningful now that a
	 * dynamic field is never unset: without it, the defaults written at creation
	 * would look indistinguishable from numbers somebody chose.
	 *
	 * Declared ABOVE the sync callbacks on purpose — it appears in their
	 * dependency arrays, which evaluate during render, so a later `const` would
	 * be in the temporal dead zone and throw.
	 */
	const dimensionsUserSet = Boolean(
		(selectedNode as any)?.data?.value?.dimensionsUserSet,
	);

	const markDimensionsUserSet = useCallback(() => {
		if (isStaticMode || dimensionsUserSet) return;
		change(`${focusIdx}.data.value.dimensionsUserSet`, true);
	}, [change, dimensionsUserSet, focusIdx, isStaticMode]);

	// Editing one dimension drives the other off the ratio. Over-budget is not
	// rejected — both values snap to the largest on-ratio pair that fits.
	const syncHeightFromWidth = useCallback(
		(raw: string | undefined) => {
			markDimensionsUserSet();
			// A circle needs equal sides by definition, in either direction.
			if (isCircleField) {
				if (!raw) return;
				const side = clampWidth(Number(raw));
				if (!Number.isFinite(side) || side <= 0) return;
				change(`${focusIdx}.attributes.height`, `${side}px`);
				return;
			}
			if (!isStaticMode) return;
			if (!raw) return setBothToAuto();
			if (!aspectRatio) return;
			const requested = Number(raw);
			if (!Number.isFinite(requested) || requested <= 0) return;
			// The field clamps on parse, so derive the height from the width that
			// actually got stored rather than from what was typed.
			const width = clampWidth(requested);
			change(`${focusIdx}.attributes.height`, `${Math.round(width / aspectRatio)}px`);
		},
		[
			aspectRatio,
			change,
			clampWidth,
			focusIdx,
			isCircleField,
			isStaticMode,
			markDimensionsUserSet,
			setBothToAuto,
		],
	);

	const syncWidthFromHeight = useCallback(
		(raw: string | undefined) => {
			markDimensionsUserSet();
			if (isCircleField) {
				if (!raw) return;
				// Max dimension is capped at Max Width even when typed as a height.
				const requested = Number(raw);
				if (!Number.isFinite(requested) || requested <= 0) return;
				const side = clampWidth(requested);
				change(`${focusIdx}.attributes.width`, `${side}px`);
				if (side !== requested) {
					change(`${focusIdx}.attributes.height`, `${side}px`);
				}
				return;
			}
			if (!isStaticMode) return;
			if (!raw) return setBothToAuto();
			if (!aspectRatio) return;
			const height = Number(raw);
			if (!Number.isFinite(height) || height <= 0) return;

			const requestedWidth = Math.round(height * aspectRatio);
			const width = clampWidth(requestedWidth);
			change(`${focusIdx}.attributes.width`, `${width}px`);

			// Height was over budget: pull it back to Max Height too.
			if (width !== requestedWidth) {
				change(`${focusIdx}.attributes.height`, `${Math.round(width / aspectRatio)}px`);
			}
		},
		[
			aspectRatio,
			change,
			clampWidth,
			focusIdx,
			isCircleField,
			isStaticMode,
			markDimensionsUserSet,
			setBothToAuto,
		],
	);

	// A new or newly cropped image starts over at Auto. Keyed on focusIdx as
	// well as src so that merely selecting another image block — which also
	// changes src — leaves that block's saved dimensions alone. The ref is kept
	// current even in dynamic mode, so switching modes can't make a stale src
	// look like a fresh upload.
	const lastSrcRef = useRef<{ focusIdx: string; src: string } | null>(null);
	useEffect(() => {
		const src = String(selectedNode?.attributes?.src || '');
		const previous = lastSrcRef.current;
		lastSrcRef.current = { focusIdx, src };

		if (!isStaticMode) return;
		if (!src || !previous) return;
		if (previous.focusIdx !== focusIdx || previous.src === src) return;

		setBothToAuto();
	}, [focusIdx, isStaticMode, selectedNode?.attributes?.src, setBothToAuto]);

	// Narrowing the column or adding padding shrinks the ceiling underneath a
	// value that was legal when it was typed. Re-clamp rather than leave the
	// panel showing a number the canvas will not honour.
	const storedWidth = toPixelNumber(selectedNode?.attributes?.width);
	const storedHeight = toPixelNumber(selectedNode?.attributes?.height);
	// A circle's sides must ALWAYS match, not just when one is edited — content
	// built before this rule, or reached by any path that skips the inputs,
	// still has to end up square.
	useEffect(() => {
		if (!isCircleField || !storedWidth || !storedHeight) return;
		if (storedWidth === storedHeight) return;
		change(`${focusIdx}.attributes.height`, `${storedWidth}px`);
	}, [change, focusIdx, isCircleField, storedHeight, storedWidth]);

	useEffect(() => {
		if (!maxWidth) return;

		if (storedWidth && storedWidth > maxWidth) {
			change(`${focusIdx}.attributes.width`, `${maxWidth}px`);
			// Follow the width with whatever rule this field obeys. A dynamic
			// logo/photo height is independent, so it is left alone.
			if (isStaticMode && aspectRatio) {
				change(`${focusIdx}.attributes.height`, `${Math.round(maxWidth / aspectRatio)}px`);
			} else if (isCircleField) {
				change(`${focusIdx}.attributes.height`, `${maxWidth}px`);
			}
			return;
		}

		// Height pinned on its own can blow the budget just as easily — but only
		// where height is tied to width (static AR, or a circle's equal sides).
		if (storedWidth || !storedHeight) return;
		const maxHeight =
			isStaticMode && aspectRatio
				? Math.round(maxWidth / aspectRatio)
				: isCircleField
					? maxWidth
					: null;
		if (maxHeight && storedHeight > maxHeight) {
			change(`${focusIdx}.attributes.height`, `${maxHeight}px`);
		}
	}, [
		aspectRatio,
		change,
		focusIdx,
		isCircleField,
		isStaticMode,
		maxWidth,
		storedHeight,
		storedWidth,
	]);

	/**
	 * In static mode the two Auto checkboxes are one control: width and height
	 * are either both Auto or neither, so they must read that way too.
	 *
	 * Needs an override because unchecking does not change what is stored —
	 * both values stay 'auto' until something is typed — so a purely derived
	 * checkbox would immediately re-tick itself.
	 */
	const [staticAutoOverride, setStaticAutoOverride] = useState<boolean | null>(null);
	const bothStoredAuto = !storedWidth && !storedHeight;

	useEffect(() => {
		setStaticAutoOverride(null);
	}, [focusIdx]);

	useEffect(() => {
		// Real numbers landed, so the derived state is trustworthy again.
		if (!bothStoredAuto) setStaticAutoOverride(null);
	}, [bothStoredAuto]);

	const staticAutoChecked = staticAutoOverride ?? bothStoredAuto;

	const onStaticAutoToggle = useCallback(
		(checked: boolean) => {
			setStaticAutoOverride(checked ? null : false);
			if (checked) setBothToAuto();
		},
		[setBothToAuto],
	);

	/**
	 * Fallback seeding for dynamic fields.
	 *
	 * A first upload only seeds dimensions nobody has chosen — swapping the
	 * placeholder later must never undo layout work. Re-opening the Cropper on
	 * the image already in the field always reseeds, because the asset's shape
	 * genuinely changed. The uploader tells us which happened; src alone can't,
	 * since both land as a new URL.
	 */
	const pendingSeedRef = useRef<'upload' | 'crop' | null>(null);

	const handleImageCommitted = useCallback(
		(reason: 'upload' | 'crop') => {
			// Static has its own rule: any new asset resets both fields to Auto.
			if (isStaticMode) return;
			pendingSeedRef.current = reason;
		},
		[isStaticMode],
	);

	useEffect(() => {
		const reason = pendingSeedRef.current;
		if (!reason) return;
		// Wait for the NEW asset to load — useImageNaturalSize clears to null on
		// src change, so a non-null size here is the image we just committed.
		if (!naturalSize || naturalSize.width <= 0 || naturalSize.height <= 0) return;

		pendingSeedRef.current = null;

		// Every dynamic field now carries system defaults from birth, so "already
		// has numbers" no longer means "the designer chose them" — ask whether
		// they were ever touched instead.
		if (reason === 'upload' && dimensionsUserSet) return;

		const width = clampWidth(naturalSize.width);
		const height = isCircleField
			? width
			: Math.round(width * (naturalSize.height / naturalSize.width));

		change(`${focusIdx}.attributes.width`, `${width}px`);
		change(`${focusIdx}.attributes.height`, `${height}px`);
	}, [
		change,
		clampWidth,
		dimensionsUserSet,
		focusIdx,
		isCircleField,
		naturalSize,
	]);

	/**
	 * A dynamic field is never blank, unset or Auto — not for one render.
	 *
	 * It is given explicit pixels the moment it becomes a recognised dynamic
	 * field, sized to the column's rendered max width and
	 * `DYNAMIC_IMAGE_DEFAULT_ASPECT` — the shape of the placeholder that is
	 * literally in the box at that point, since there is no asset to measure.
	 * Same shape for every field kind. A real fallback supersedes this via the
	 * seeding effect above, and the designer supersedes that.
	 *
	 * Also repairs pre-existing content, which is why it is a standing effect
	 * rather than a one-shot on creation.
	 */
	useEffect(() => {
		if (isStaticMode || !dynamicFieldKind || !maxWidth) return;
		// Let a pending fallback seed win rather than writing twice.
		if (pendingSeedRef.current) return;
		if (storedWidth && storedHeight) return;
		// A designer clearing a field to retype it has no stored value for a
		// moment, which from here is indistinguishable from a block that never had
		// one. Seeding into that gap refills the input on the very keystroke that
		// emptied it, which is what made backspace look blocked — the only way to
		// change a number was select-all-and-overwrite.
		//
		// Once the dimensions have been touched, the input owns the empty case:
		// AutoCheckboxInput defers the "can't be blank" rule to blur and restores
		// the last real number, so nothing is left unset. This effect goes back to
		// what it is for — blocks that have never carried dimensions at all.
		if (dimensionsUserSet) return;

		const aspect =
			naturalSize && naturalSize.height > 0
				? naturalSize.width / naturalSize.height
				: DYNAMIC_IMAGE_DEFAULT_ASPECT;

		const width = storedWidth ? clampWidth(storedWidth) : maxWidth;
		const height = isCircleField ? width : Math.round(width / aspect);

		change(`${focusIdx}.attributes.width`, `${width}px`);
		change(`${focusIdx}.attributes.height`, `${height}px`);
	}, [
		change,
		clampWidth,
		dimensionsUserSet,
		dynamicFieldKind,
		focusIdx,
		isCircleField,
		isStaticMode,
		maxWidth,
		naturalSize,
		storedHeight,
		storedWidth,
	]);

	const ensureDynamicImageId = useCallback(() => {
		if (dynamicImageIdInput.value || !focusIdx) return dynamicImageIdInput.value;
		const id = `img_dyn_${uuidv4().replace(/-/g, '').slice(0, 12)}`;
		change(`${focusIdx}.data.value.dynamicImageId`, id);
		return id;
	}, [change, dynamicImageIdInput.value, focusIdx]);

	const handleDynamicImageTagChange = useCallback(
		(rawValue: string) => {
			const normalized = String(rawValue || '').trim();
			if (!normalized) {
				dynamicImageTagInput.onChange('');
				return;
			}
			const clean = normalized.replace(/^\{\{|\}\}$/g, '').trim();
			if (!clean) return;
			ensureDynamicImageId();
			dynamicImageTagInput.onChange(`{{${clean}}}`);
		},
		[dynamicImageTagInput, ensureDynamicImageId],
	);

	const handleModeChange = useCallback(
		(nextMode: ImageMode) => {
			if (forceMode) return;
			imageTypeInput.onChange(nextMode);

			if (nextMode === 'static' && focusIdx) {
				change(`${focusIdx}.data.value.selectedListingId`, '');
				change(`${focusIdx}.data.value.selectedListing`, null);
				change(`${focusIdx}.data.value.dynamicImageTag`, '');
				change(`${focusIdx}.data.value.dynamicImageId`, '');
			}

			if (nextMode === 'dynamic' && focusIdx) {
				change(`${focusIdx}.data.value.selectedListingId`, '');
				change(`${focusIdx}.data.value.selectedListing`, null);
			}
		},
		[change, focusIdx, forceMode, imageTypeInput],
	);
	const staticModeHasListingMergeTags = useMemo(() => {
		return (
			usesListingMergeTags(String(selectedNode?.attributes?.src || '')) ||
			usesListingMergeTags(String(selectedNode?.attributes?.href || ''))
		);
	}, [selectedNode?.attributes?.href, selectedNode?.attributes?.src]);

	const dynamicModeHasListingMergeTags = useMemo(() => {
		return (
			usesListingMergeTags(String(normalizedDynamicTag || '')) ||
			usesListingMergeTags(String(selectedNode?.attributes?.href || '')) ||
			usesListingMergeTags(String(selectedNode?.attributes?.src || ''))
		);
	}, [
		normalizedDynamicTag,
		selectedNode?.attributes?.href,
		selectedNode?.attributes?.src,
	]);

	const showListingSettings =
		effectiveMode === 'dynamic'
			? dynamicModeHasListingMergeTags
			: staticModeHasListingMergeTags;
	useClearAttachedListingWhenUnused(selectedNode, showListingSettings);
	const defaultActiveKey = useMemo(
		() => (showListingSettings ? ['-1', '1', '0', '3'] : ['1', '0', '3']),
		[showListingSettings],
	);

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={defaultActiveKey}
			style={{ padding: 0 }}
		>
			{showListingSettings && (
				<Collapse.Item
					name='-1'
					header={t('Listing Settings')}
					contentStyle={{ padding: 0 }}
				>
					<AttachedListingSection
						observedValues={[
							selectedNode?.attributes?.src,
							selectedNode?.attributes?.href,
							selectedNode?.data?.value?.dynamicImageTag,
						]}
					/>
				</Collapse.Item>
			)}
			<Collapse.Item
				name='1'
				header={contentHeader || t('Content Block Settings')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3 pt-4'>
					{!hideModeToggle && (
						<div style={{ marginBottom: '12px' }}>
							<Space className='w-100 d-flex justify-content-between align-items-center'>
								<span
									className='fw-bold text-capitalize'
									style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
								>
									{t('Image type')}
									<BayTooltip
										overlay={t(
											'Choose static or dynamic mode. Dynamic mode resolves a merge tag and can fall back to the uploaded image.',
										)}
										placement='top'
									>
										<span
											style={{
												display: 'inline-flex',
												alignItems: 'center',
												color: '#86909c',
												cursor: 'help',
											}}
										>
											<BsQuestionCircle size={12} />
										</span>
									</BayTooltip>
								</span>
							</Space>
							<div style={{ marginTop: '8px' }}>
								<BayPillToggle<'static' | 'dynamic'>
									ariaLabel={t('Image type')}
									size='md'
									options={[
										{ value: 'static', label: t('Static') },
										{ value: 'dynamic', label: t('Dynamic') },
									]}
									value={effectiveMode}
									onChange={handleModeChange}
								/>
							</div>
						</div>
					)}

					{/* Source first, then the fallback it backs up. */}
					{effectiveMode === 'dynamic' && (
						<div style={{ marginBottom: '8px' }}>
							<Space className='my-2 w-100 d-flex justify-content-between align-items-center'>
								<span className='fw-bold text-capitalize'>
									{t('Dynamic Image Source')}
								</span>
								<Space>
									<MergeTags
										value={normalizedDynamicTag}
										onChange={handleDynamicImageTagChange}
										context='image-src'
										showSearch
									>
										<span className='dynamicFieldDropdown text-secondary cursor-pointer d-flex gap-2'>
											<span>{`{ }`}</span>
											<span className='dynamic-field'>{t('Dynamic Fields')}</span>
										</span>
									</MergeTags>
								</Space>
							</Space>
							{/* Display-only. The value is chosen from the Dynamic Fields menu
							    above and stored as the raw tag; typing here let anything
							    through, and a tag the dictionary doesn't know renders nothing
							    at send time. readOnly rather than disabled so the field stays
							    focusable and announced — it is not an inactive control. */}
							{/* The span is load-bearing. Arco's Input IS a forwardRef, so
							    BayTooltip's canHoldRef says yes and clones the ref straight
							    onto it — but Arco forwards that ref to an imperative handle
							    ({ dom, focus, blur }), not a DOM node. Floating UI then has
							    nothing with getBoundingClientRect to measure and pins the
							    tooltip to the top-left corner of the window. Anchoring to a
							    real element is the fix; display:block keeps the Input's
							    full width. */}
							<BayTooltip
								overlay={t(
									'Choose a field from the Dynamic Fields menu above. This cannot be typed in.',
								)}
								placement='top'
							>
								<span style={{ display: 'block' }}>
									<Input
										readOnly
										allowClear
										value={getMergeTagDisplayLabel(normalizedDynamicTag)}
										placeholder={t('No dynamic field selected')}
										onClear={() => handleDynamicImageTagChange('')}
									/>
								</span>
							</BayTooltip>
							<hr
								style={{
									margin: '8px 0 10px',
									border: 0,
									borderTop: '1px solid #f2f3f5',
								}}
							/>
						</div>
					)}

					<div style={{ marginBottom: '12px' }}>
						<ImageUploaderField
							headerTitle={
								effectiveMode === 'dynamic' ? t('Fallback Image') : t('Image Upload')
							}
							label={t('src')}
							labelHidden
							name={`${focusIdx}.attributes.src`}
							uploadHandler={onUploadImage}
							context='image-src'
							isImageCropperAllowed={true}
							showLink={false}
							onImageCommitted={handleImageCommitted}
							uploadDisabled={effectiveMode === 'dynamic' && !normalizedDynamicTag}
						/>
					</div>

					<Link
						context='image-link'
						isCropperAllowed={true}
					/>
				</div>
			</Collapse.Item>

			<Collapse.Item
				name='0'
				header={dimensionHeader}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3 pb-4'>
					{hasImageToSize && (
					<Grid.Row
						gutter={16}
						style={{ marginBottom: '8px' }}
					>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<Width
									config={imageHeightAdapter}
									placeholder={widthPlaceholder}
									autoEnabled
									// Hard ceiling in both modes: the column bound.
									maxPx={maxWidth ?? undefined}
									showPlaceholderWhenAuto={Boolean(widthPlaceholder)}
									// A cover field crops to fill, so it needs a real number.
									allowAuto={!requiresExplicitSize}
									disabled={staticSizingUnavailable}
									autoChecked={isStaticMode ? staticAutoChecked : undefined}
									onAutoToggle={isStaticMode ? onStaticAutoToggle : undefined}
									onValueCommitted={syncHeightFromWidth}
								/>
							</div>
						</Grid.Col>
						<Grid.Col span={12}>
							<div style={{ paddingLeft: '8px' }}>
								<Height
									config={imageHeightAdapter}
									placeholder={heightPlaceholder}
									showPlaceholderWhenAuto={Boolean(heightPlaceholder)}
									allowAuto={!requiresExplicitSize}
									disabled={staticSizingUnavailable}
									autoChecked={isStaticMode ? staticAutoChecked : undefined}
									onAutoToggle={isStaticMode ? onStaticAutoToggle : undefined}
									onValueCommitted={syncWidthFromHeight}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					)}
					<Padding showResetAll />
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 12,
							marginTop: '1rem',
						}}
					>
						<BaySwitch
							className='mb-0'
							checked={Boolean(fluidOnMobileInput.value)}
							onChange={() =>
								fluidOnMobileInput.onChange(fluidOnMobileInput.value ? undefined : 'true')
							}
						/>
						<span
							style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}
						>
							{t('Full width on mobile')}
						</span>
					</div>
				</div>
			</Collapse.Item>

			<Collapse.Item
				name='3'
				header={t('Styling')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3 pt-4'>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<ContainerBackgroundColor title={t('Background color')} />
						</Grid.Col>
					</Grid.Row>
					<Align />
					<TextStyle
						variation='strong'
						style={{ marginBottom: '12px', marginTop: '16px', display: 'block' }}
					>
						{t('Border')}
					</TextStyle>
					<Grid.Row gutter={16}>
						<Grid.Col span={10}>
							<BorderColor secondary />
						</Grid.Col>
						<Grid.Col span={8}>
							<BorderWidth
								secondary
								config={pixelAdapter}
								label={t('Thickness (px)')}
							/>
						</Grid.Col>
						<Grid.Col span={6}>
							<BorderStyle secondary />
						</Grid.Col>
					</Grid.Row>
				</div>
			</Collapse.Item>

			<Collapse.Item
				name='4'
				header={t('Extra')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3 pt-4'>
					<TextField
						label={t('title')}
						name={`${focusIdx}.attributes.title`}
					/>
					<TextField
						label={t('alt')}
						name={`${focusIdx}.attributes.alt`}
					/>
					<TextField
						label={t('class name')}
						name={`${focusIdx}.attributes.css-class`}
					/>
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
